import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LogoMark, Wordmark } from "@/components/Reveal";
import { LEGAL_LINKS, CONTACT_EMAIL } from "@/data/legal";
import { useCountry } from "@/context/CountryContext";
import { theftStatsFor } from "@/data/theftStats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildColumns = (isUS) => {
  const stats = theftStatsFor(isUS);
  return [
  {
    title: "Shop",
    links: [
      { label: "All bundles", to: "/shop" },
      { label: "Vehicle trackers", to: "/vehicle-tracker" },
      { label: "Trailer trackers", to: "/trailer-tracker" },
      { label: "Equipment trackers", to: "/equipment-tracker" },
      { label: "Luggage trackers", to: "/luggage-tracker" },
    ],
  },
  {
    title: "Tracksy",
    links: [
      { label: "Tracksy Vehicle", to: "/vehicles" },
      { label: "Tracksy Fleet", to: "/fleet" },
      { label: "Tracksy Equipment", to: "/equipment" },
      { label: "Tracksy Personal", to: "/personal" },
      { label: "Magnetic tracker", to: "/magnetic-tracker" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "How Tracksy works", to: "/how-it-works" },
      { label: "FAQ", to: "/support" },
      { label: "Placement guide", to: "/placement-guide" },
      { label: "Shipping, returns & warranty", to: "/support#policies" },
      { label: "Request fleet pricing", to: "/fleet#enquiry" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Tracksy", to: "/about" },
      { label: isUS ? "US theft stats" : "Australian theft stats", to: stats.canonical },
      ...LEGAL_LINKS,
    ],
  },
  ];
};

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { isUS } = useCountry();
  const COLUMNS = buildColumns(isUS);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error();
      toast.success("You're on the list", { description: "Product news and new categories, first." });
      setEmail("");
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="border-t border-white/10 bg-tracksy-dark px-4 py-16 text-sm text-white/60 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2">
              <LogoMark className="h-8 w-8" />
              <Wordmark dark />
            </div>
            <p className="mt-4 max-w-sm leading-relaxed">
              Track What Matters. Compact magnetic tracking for vehicles, trailers, fleets,
              equipment and everyday essentials.
            </p>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-contact-email" className="mt-4 inline-block text-sm text-white/70 transition-colors hover:text-tracksy-orange">
              {CONTACT_EMAIL}
            </a>
            <form data-testid="newsletter-form" onSubmit={subscribe} className="mt-6 flex max-w-sm items-center gap-2">
              <input
                type="email"
                data-testid="newsletter-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for product updates"
                className="h-11 flex-1 rounded-full border border-white/15 bg-white/5 px-4 text-sm text-white placeholder:text-white/40 focus:border-tracksy-orange focus:outline-none"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-button"
                disabled={loading}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-tracksy-orange text-white transition-colors hover:bg-tracksy-orangeHover disabled:opacity-60"
                aria-label="Subscribe"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4 lg:col-span-8">
            {COLUMNS.map((c) => (
              <div key={c.title}>
                <p className="mb-4 text-xs font-bold uppercase tracking-widest text-white">{c.title}</p>
                <ul className="space-y-2.5">
                  {c.links.map((l) => (
                    <li key={l.to + l.label}>
                      <Link to={l.to} className="transition-colors hover:text-tracksy-orange">{l.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs sm:flex-row sm:items-start sm:justify-between">
          <p>© 2026 Tracksy. All rights reserved.</p>
          <p className="max-w-2xl leading-relaxed sm:text-right">
            Tracksy is a compact Bluetooth tracker that works with Apple Find My and Google Find Hub. Apple, Find My, iPhone and iPad are trademarks of Apple Inc. Google and Find Hub are trademarks of Google LLC. Tracksy is an independent brand and is not affiliated with Apple Inc. or Google LLC.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
